import React, { useState, useEffect, useCallback } from 'react';
import './FilterBuilder.css';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
} from 'reactstrap';

// ─── Operator catalog ─────────────────────────────────────────────────────────

const OPS = {
  string: [
    { value: '$eq', label: 'equals', symbol: '=' },
    { value: '$ne', label: 'not equals', symbol: '≠' },
    { value: '$like', label: 'like', symbol: '~' },
    { value: '$iLike', label: 'like (case-insensitive)', symbol: '~i' },
    { value: '$notLike', label: 'not like', symbol: '!~' },
    { value: '$startsWith', label: 'starts with', symbol: '^' },
    { value: '$endsWith', label: 'ends with', symbol: '$' },
    { value: '$substring', label: 'contains substring', symbol: '∋' },
    { value: '$in', label: 'in', symbol: '∈' },
    { value: '$notIn', label: 'not in', symbol: '∉' },
    { value: '$is', label: 'is null', symbol: '∅' },
  ],
  number: [
    { value: '$eq', label: 'equals', symbol: '=' },
    { value: '$ne', label: 'not equals', symbol: '≠' },
    { value: '$gt', label: 'greater than', symbol: '>' },
    { value: '$gte', label: 'greater than or equal', symbol: '≥' },
    { value: '$lt', label: 'less than', symbol: '<' },
    { value: '$lte', label: 'less than or equal', symbol: '≤' },
    { value: '$between', label: 'between', symbol: '↔' },
    { value: '$notBetween', label: 'not between', symbol: '↮' },
    { value: '$in', label: 'in', symbol: '∈' },
    { value: '$notIn', label: 'not in', symbol: '∉' },
    { value: '$is', label: 'is null', symbol: '∅' },
  ],
  date: [
    { value: '$eq', label: 'on', symbol: '=' },
    { value: '$ne', label: 'not on', symbol: '≠' },
    { value: '$gt', label: 'after', symbol: '>' },
    { value: '$gte', label: 'on or after', symbol: '≥' },
    { value: '$lt', label: 'before', symbol: '<' },
    { value: '$lte', label: 'on or before', symbol: '≤' },
    { value: '$between', label: 'between', symbol: '↔' },
    { value: '$notBetween', label: 'not between', symbol: '↮' },
    { value: '$is', label: 'is null', symbol: '∅' },
  ],
  enum: [
    { value: '$eq', label: 'equals', symbol: '=' },
    { value: '$ne', label: 'not equals', symbol: '≠' },
    { value: '$in', label: 'in', symbol: '∈' },
    { value: '$notIn', label: 'not in', symbol: '∉' },
    { value: '$is', label: 'is null', symbol: '∅' },
  ],
  boolean: [
    { value: '$eq', label: 'is', symbol: '=' },
    { value: '$ne', label: 'is not', symbol: '≠' },
    { value: '$is', label: 'is null', symbol: '∅' },
  ],
};

const RANGE_OPS = new Set(['$between', '$notBetween']);
const ARRAY_OPS = new Set(['$in', '$notIn']);
const NULL_OPS = new Set(['$is']);

// ─── Query builder helpers ────────────────────────────────────────────────────

function coerce(raw, fieldType) {
  if (fieldType === 'number') return raw === '' ? '' : Number(raw);
  if (fieldType === 'boolean') return raw === 'true';
  return raw;
}

function resolveValue(value, operator, fieldType) {
  if (NULL_OPS.has(operator)) return null;
  if (RANGE_OPS.has(operator)) {
    const [a, b] = Array.isArray(value) ? value : ['', ''];
    return [coerce(a, fieldType), coerce(b, fieldType)];
  }
  if (ARRAY_OPS.has(operator)) {
    if (Array.isArray(value)) return value.map((v) => coerce(v, fieldType));
    return String(value)
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
      .map((v) => coerce(v, fieldType));
  }
  return coerce(value, fieldType);
}

function buildClause(condition) {
  const { field, operator, value, fieldType } = condition;
  return { [field]: { [operator]: resolveValue(value, operator, fieldType) } };
}

export function buildWhere(conditions, logicalOp) {
  if (!conditions.length) return {};
  const parts = conditions.map(buildClause);
  if (parts.length === 1) return parts[0];
  return { [logicalOp]: parts };
}

// ─── Input helpers ────────────────────────────────────────────────────────────

function htmlInputType(fieldType) {
  if (fieldType === 'date') return 'datetime-local';
  if (fieldType === 'number') return 'number';
  return 'text';
}

// Enum `options` accept either a bare string (display text === stored value) or
// a `{ value, label }` pair, for vocabularies where the two differ — e.g. the
// approval actions fetched from the API, which send `order:delete` but display
// "Delete order". Backward-compatible: string options behave exactly as before.
function optValue(opt) {
  return opt && typeof opt === 'object' ? opt.value : opt;
}

function optLabel(opt) {
  return opt && typeof opt === 'object' ? opt.label : opt;
}

// Value -> display text for one enum field; falls back to the raw value when the
// vocabulary doesn't know it (e.g. a saved filter naming a retired action).
function labelForOption(fieldDef, value) {
  const options = (fieldDef && fieldDef.options) || [];
  const hit = options.find((o) => String(optValue(o)) === String(value));
  return hit ? String(optLabel(hit)) : String(value);
}

// datetime-local inputs require "yyyy-MM-ddTHH:mm"; strip timezone + sub-minute precision.
function normalizeDatetime(val) {
  if (!val) return '';
  return String(val)
    .replace(/Z$/, '')
    .replace(/[+-]\d{2}:\d{2}$/, '')
    .slice(0, 16);
}

// ─── FilterConditionModal ─────────────────────────────────────────────────────

const BLANK = { field: '', fieldType: '', operator: '', value: '' };

function FilterConditionModal({ isOpen, fields, initial, onSave, onClose }) {
  const [cond, setCond] = useState(BLANK);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      setCond(initial ? { ...initial } : { ...BLANK });
      setErrors({});
    }
  }, [isOpen, initial]);

  const selectedField = fields.find((f) => f.key === cond.field) || null;
  const availableOps = selectedField ? OPS[selectedField.type] || [] : [];

  const patch = useCallback(
    (key, val) => {
      setCond((prev) => {
        const next = { ...prev, [key]: val };
        if (key === 'field') {
          const f = fields.find((ff) => ff.key === val);
          next.fieldType = f ? f.type : '';
          next.operator = '';
          next.value = '';
        }
        if (key === 'operator') {
          next.value = RANGE_OPS.has(val) ? ['', ''] : '';
        }
        return next;
      });
      setErrors((e) => ({ ...e, [key]: '', valueFrom: '', valueTo: '' }));
    },
    [fields]
  );

  const validate = () => {
    const e = {};
    if (!cond.field) e.field = 'Select a field';
    if (!cond.operator) e.operator = 'Select an operator';
    if (!NULL_OPS.has(cond.operator)) {
      if (RANGE_OPS.has(cond.operator)) {
        const [a, b] = Array.isArray(cond.value) ? cond.value : ['', ''];
        if (String(a).trim() === '') e.valueFrom = 'Required';
        if (String(b).trim() === '') e.valueTo = 'Required';
      } else if (!String(cond.value).trim()) {
        e.value = 'Required';
      }
    }
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    onSave(cond);
  };

  // ── Value input renderer ────────────────────────────────────────────────────

  const renderValue = () => {
    if (!cond.operator) return null;

    if (NULL_OPS.has(cond.operator)) {
      return (
        <div className="fb-null-hint">
          Matches records where{' '}
          <strong>{selectedField && selectedField.label}</strong> is{' '}
          <code>NULL</code>. No value required.
        </div>
      );
    }

    if (RANGE_OPS.has(cond.operator)) {
      const [from, to] = Array.isArray(cond.value) ? cond.value : ['', ''];
      const type = htmlInputType(cond.fieldType);
      const isDate = cond.fieldType === 'date';
      return (
        <div>
          <Row className="align-items-center" style={{ margin: 0 }}>
            <Col style={{ padding: '0 6px 0 0' }}>
              <Input
                type={type}
                placeholder="From"
                value={isDate ? normalizeDatetime(from) : from}
                onChange={(e) => patch('value', [e.target.value, to])}
                invalid={errors.valueFrom ? true : undefined}
              />
              {errors.valueFrom && (
                <div className="fb-error">{errors.valueFrom}</div>
              )}
            </Col>
            <Col
              xs="auto"
              style={{ padding: '0 4px', color: '#9a9a9a', fontWeight: 600 }}
            >
              and
            </Col>
            <Col style={{ padding: '0 0 0 6px' }}>
              <Input
                type={type}
                placeholder="To"
                value={isDate ? normalizeDatetime(to) : to}
                onChange={(e) => patch('value', [from, e.target.value])}
                invalid={errors.valueTo ? true : undefined}
              />
              {errors.valueTo && (
                <div className="fb-error">{errors.valueTo}</div>
              )}
            </Col>
          </Row>
        </div>
      );
    }

    if (ARRAY_OPS.has(cond.operator)) {
      if (
        selectedField &&
        selectedField.type === 'enum' &&
        selectedField.options &&
        selectedField.options.length
      ) {
        const selected = Array.isArray(cond.value) ? cond.value : [];
        return (
          <div>
            <div className="fb-checkbox-grid">
              {selectedField.options.map((opt) => {
                const val = String(optValue(opt));
                return (
                  <label key={val} className="fb-checkbox-item">
                    <input
                      type="checkbox"
                      value={val}
                      checked={selected.includes(val)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...selected, val]
                          : selected.filter((s) => s !== val);
                        patch('value', next);
                      }}
                    />
                    <span>{optLabel(opt)}</span>
                  </label>
                );
              })}
            </div>
            {errors.value && <div className="fb-error">{errors.value}</div>}
          </div>
        );
      }
      const displayVal = Array.isArray(cond.value)
        ? cond.value.join(', ')
        : cond.value;
      return (
        <div>
          <Input
            type="textarea"
            rows={3}
            placeholder="value1, value2, value3"
            value={displayVal}
            onChange={(e) => patch('value', e.target.value)}
            invalid={errors.value ? true : undefined}
          />
          {errors.value && <div className="fb-error">{errors.value}</div>}
          <div className="fb-hint">Separate values with commas</div>
        </div>
      );
    }

    if (
      selectedField &&
      selectedField.type === 'enum' &&
      selectedField.options &&
      selectedField.options.length
    ) {
      return (
        <div>
          <Input
            type="select"
            value={String(cond.value)}
            onChange={(e) => patch('value', e.target.value)}
            invalid={errors.value ? true : undefined}
          >
            <option value="">Choose a value…</option>
            {selectedField.options.map((opt) => (
              <option key={String(optValue(opt))} value={optValue(opt)}>
                {optLabel(opt)}
              </option>
            ))}
          </Input>
          {errors.value && <div className="fb-error">{errors.value}</div>}
        </div>
      );
    }

    if (selectedField && selectedField.type === 'boolean') {
      return (
        <div>
          <Input
            type="select"
            value={String(cond.value)}
            onChange={(e) => patch('value', e.target.value)}
            invalid={errors.value ? true : undefined}
          >
            <option value="">Choose…</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </Input>
          {errors.value && <div className="fb-error">{errors.value}</div>}
        </div>
      );
    }

    // Relative-date presets (only when the field config supplies them, e.g. the
    // Vend Requests screen). Offered for scalar comparison ops; range/null ops
    // keep their dedicated inputs handled above. Backward-compatible: fields
    // without `presets` never reach this branch.
    if (
      selectedField &&
      selectedField.type === 'date' &&
      selectedField.presets &&
      selectedField.presets.length
    ) {
      const isPreset =
        typeof cond.value === 'string' && cond.value.charAt(0) === '@';
      return (
        <div>
          <Input
            type="select"
            value={isPreset ? cond.value : ''}
            onChange={(e) => patch('value', e.target.value)}
          >
            <option value="">Exact date/time…</option>
            {selectedField.presets.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Input>
          {!isPreset && (
            <Input
              type="datetime-local"
              style={{ marginTop: 8 }}
              value={normalizeDatetime(cond.value)}
              onChange={(e) => patch('value', e.target.value)}
              invalid={errors.value ? true : undefined}
            />
          )}
          {errors.value && <div className="fb-error">{errors.value}</div>}
        </div>
      );
    }

    const placeholder =
      cond.operator === '$like' || cond.operator === '$iLike'
        ? '%value%'
        : cond.operator === '$startsWith'
        ? 'prefix…'
        : cond.operator === '$endsWith'
        ? '…suffix'
        : 'Enter a value';

    return (
      <div>
        <Input
          type={htmlInputType(cond.fieldType)}
          placeholder={placeholder}
          value={
            cond.fieldType === 'date'
              ? normalizeDatetime(cond.value)
              : String(cond.value)
          }
          onChange={(e) => patch('value', e.target.value)}
          invalid={errors.value ? true : undefined}
        />
        {errors.value && <div className="fb-error">{errors.value}</div>}
        {(cond.operator === '$like' || cond.operator === '$iLike') && (
          <div className="fb-hint">
            Use <code>%</code> as a wildcard — e.g. <code>%john%</code> matches
            anywhere.
          </div>
        )}
      </div>
    );
  };

  // ── Stepper indicators ──────────────────────────────────────────────────────

  const step = !cond.field ? 1 : !cond.operator ? 2 : 3;

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="md">
      <ModalHeader
        toggle={onClose}
        style={{ borderBottom: '2px solid #f96332' }}
      >
        {initial ? 'Edit Condition' : 'Add Filter Condition'}
      </ModalHeader>

      <ModalBody style={{ padding: '20px 24px' }}>
        {/* Step tracker */}
        <div className="fb-steps">
          {['Field', 'Operator', 'Value'].map((label, i) => {
            const n = i + 1;
            const done = step > n;
            const active = step === n;
            return (
              <React.Fragment key={label}>
                <div
                  className={`fb-step ${done ? 'done' : ''} ${
                    active ? 'active' : ''
                  }`}
                >
                  <div className="fb-step-dot">
                    {done ? <span>&#10003;</span> : n}
                  </div>
                  <span className="fb-step-label">{label}</span>
                </div>
                {i < 2 && (
                  <div className={`fb-step-line ${done ? 'done' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Field */}
        <FormGroup style={{ marginTop: 20 }}>
          <Label className="fb-label">Field</Label>
          <Input
            type="select"
            value={cond.field}
            onChange={(e) => patch('field', e.target.value)}
            invalid={errors.field ? true : undefined}
          >
            <option value="">Select a field…</option>
            {fields.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </Input>
          {errors.field && <div className="fb-error">{errors.field}</div>}
        </FormGroup>

        {/* Operator (appears once field is chosen) */}
        {cond.field && (
          <FormGroup>
            <Label className="fb-label">Operator</Label>
            <Input
              type="select"
              value={cond.operator}
              onChange={(e) => patch('operator', e.target.value)}
              invalid={errors.operator ? true : undefined}
            >
              <option value="">Select an operator…</option>
              {availableOps.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label} ({op.value})
                </option>
              ))}
            </Input>
            {errors.operator && (
              <div className="fb-error">{errors.operator}</div>
            )}
          </FormGroup>
        )}

        {/* Value (appears once operator is chosen) */}
        {cond.operator && (
          <FormGroup>
            <Label className="fb-label">Value</Label>
            {renderValue()}
          </FormGroup>
        )}

        {/* Live query preview */}
        {cond.field && cond.operator && (
          <div className="fb-preview">
            <span className="fb-preview-label">Query preview</span>
            <code>
              {JSON.stringify(
                buildClause({
                  ...cond,
                  value: resolveValue(
                    cond.value,
                    cond.operator,
                    cond.fieldType
                  ),
                }),
                null,
                2
              )}
            </code>
          </div>
        )}
      </ModalBody>

      <ModalFooter style={{ borderTop: '1px solid #e3e3e3' }}>
        <Button color="secondary" outline size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          style={{
            backgroundColor: '#f96332',
            borderColor: '#f96332',
            color: '#fff',
          }}
        >
          {initial ? 'Update Condition' : 'Add Condition'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Main FilterBuilder ───────────────────────────────────────────────────────

const uid = () => Date.now() + Math.floor(Math.random() * 1000);

/**
 * @param {Array}    fields            — [{ key, label, type, options?, presets? }]
 *                                       type: 'string'|'number'|'date'|'enum'|'boolean'
 *                                       options (enum): string[] or [{ value, label }]
 * @param {Array}    initialConditions — pre-populated conditions (e.g. restored from URL)
 * @param {string}   initialLogicalOp  — '$and' | '$or'
 * @param {Function} onChange          — called as onChange(where, conditions, logicalOp)
 */
export default function FilterBuilder({
  fields = [],
  initialConditions,
  initialLogicalOp,
  onChange,
  onSaveFilter,
}) {
  const [conditions, setConditions] = useState(initialConditions || []);
  const [logicalOp, setLogicalOp] = useState(initialLogicalOp || '$and');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showQuery, setShowQuery] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [saveDescription, setSaveDescription] = useState('');

  const notify = useCallback(
    (conds, op) => {
      onChange && onChange(buildWhere(conds, op), conds, op);
    },
    [onChange]
  );

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSave = (cond) => {
    let next;
    if (editing) {
      next = conditions.map((c) =>
        c.id === editing.id ? { ...cond, id: editing.id } : c
      );
    } else {
      next = [...conditions, { ...cond, id: uid() }];
    }
    setConditions(next);
    closeModal();
    notify(next, logicalOp);
  };

  const remove = (id) => {
    const next = conditions.filter((c) => c.id !== id);
    setConditions(next);
    notify(next, logicalOp);
  };

  const changeLogical = (op) => {
    setLogicalOp(op);
    notify(conditions, op);
  };

  const clearAll = () => {
    setConditions([]);
    onChange && onChange({}, [], logicalOp);
  };

  const where = buildWhere(conditions, logicalOp);

  return (
    <div className="filter-builder">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="fb-header">
        <div className="fb-header-left">
          <i className="now-ui-icons ui-1_settings-gear-63 fb-icon" />
          <span className="fb-title">Filters</span>
          {conditions.length > 0 && (
            <span className="fb-count">{conditions.length}</span>
          )}
        </div>
        <div className="fb-header-right">
          {conditions.length > 0 && (
            <>
              <button
                className="fb-btn-ghost"
                onClick={() => setShowQuery((v) => !v)}
              >
                {showQuery ? 'Hide query' : 'Show query'}
              </button>
              <button className="fb-btn-ghost fb-btn-danger" onClick={clearAll}>
                Clear all
              </button>
            </>
          )}
          <button className="fb-btn-primary" onClick={openAdd}>
            + Add Filter
          </button>
        </div>
      </div>

      {/* ── Logical combinator ─────────────────────────────────────────────── */}
      {conditions.length > 1 && (
        <div className="fb-combinator">
          <span className="fb-combinator-label">Match</span>
          <div className="fb-toggle">
            <button
              className={`fb-toggle-btn ${
                logicalOp === '$and' ? 'active' : ''
              }`}
              onClick={() => changeLogical('$and')}
            >
              ALL (AND)
            </button>
            <button
              className={`fb-toggle-btn ${logicalOp === '$or' ? 'active' : ''}`}
              onClick={() => changeLogical('$or')}
            >
              ANY (OR)
            </button>
          </div>
          <span className="fb-combinator-label">
            of the following conditions
          </span>
        </div>
      )}

      {/* ── Condition chips ─────────────────────────────────────────────────── */}
      <div className="fb-chips-area">
        {conditions.length === 0 ? (
          <div className="fb-empty">
            <div className="fb-empty-icon">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#dddddd"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
            </div>
            <span>
              No filters applied. Click <strong>+ Add Filter</strong> to refine
              results.
            </span>
          </div>
        ) : (
          <div className="fb-chips">
            {conditions.map((cond, idx) => {
              const fieldDef = fields.find((f) => f.key === cond.field) || {};
              // Enum chips read back as labels; every other field type is its
              // own display text.
              const display = (v) =>
                fieldDef.type === 'enum'
                  ? labelForOption(fieldDef, v)
                  : String(v);
              return (
                <React.Fragment key={cond.id}>
                  {idx > 0 && (
                    <span className="fb-connector">
                      {logicalOp === '$and' ? 'AND' : 'OR'}
                    </span>
                  )}
                  <div
                    className="fb-chip"
                    onClick={() => openEdit(cond)}
                    title="Click to edit"
                  >
                    <span className="fb-chip-field">
                      {fieldDef.label || cond.field}
                    </span>
                    <span className="fb-chip-op">
                      {(
                        (OPS[cond.fieldType] || []).find(
                          (o) => o.value === cond.operator
                        ) || {}
                      ).symbol || cond.operator}
                    </span>
                    <span className="fb-chip-value">
                      {NULL_OPS.has(cond.operator)
                        ? 'null'
                        : RANGE_OPS.has(cond.operator)
                        ? (() => {
                            const [a, b] = Array.isArray(cond.value)
                              ? cond.value
                              : ['', ''];
                            return `${a} – ${b}`;
                          })()
                        : ARRAY_OPS.has(cond.operator)
                        ? (() => {
                            const arr = (
                              Array.isArray(cond.value)
                                ? cond.value
                                : String(cond.value)
                                    .split(',')
                                    .map((v) => v.trim())
                                    .filter(Boolean)
                            ).map(display);
                            return arr.length > 2
                              ? `${arr.slice(0, 2).join(', ')} +${
                                  arr.length - 2
                                }`
                              : arr.join(', ');
                          })()
                        : display(cond.value)}
                    </span>
                    <button
                      className="fb-chip-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(cond.id);
                      }}
                      title="Remove condition"
                    >
                      ×
                    </button>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Save filter CTA ────────────────────────────────────────────────── */}
      {onSaveFilter && conditions.length > 0 && (
        <div className="fb-save-row">
          {showSaveForm ? (
            <div className="fb-save-form">
              <FormGroup>
                <Label className="fb-save-label" for="fb-save-label-input">
                  Filter name
                </Label>
                <Input
                  id="fb-save-label-input"
                  type="text"
                  bsSize="sm"
                  placeholder="e.g. Failed orders — last 30 days"
                  value={saveLabel}
                  onChange={function (e) {
                    setSaveLabel(e.target.value);
                  }}
                  autoFocus
                />
              </FormGroup>
              <FormGroup>
                <Label className="fb-save-label" for="fb-save-desc-input">
                  Description
                </Label>
                <Input
                  id="fb-save-desc-input"
                  type="text"
                  bsSize="sm"
                  placeholder="Short note about what this filter shows"
                  value={saveDescription}
                  onChange={function (e) {
                    setSaveDescription(e.target.value);
                  }}
                />
              </FormGroup>
              <div className="fb-save-form-actions">
                <Button
                  color="primary"
                  size="sm"
                  disabled={!saveLabel.trim() || !saveDescription.trim()}
                  onClick={function () {
                    if (!saveLabel.trim() || !saveDescription.trim()) return;
                    onSaveFilter({
                      label: saveLabel.trim(),
                      description: saveDescription.trim(),
                    });
                    setShowSaveForm(false);
                    setSaveLabel('');
                    setSaveDescription('');
                  }}
                >
                  Save
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  outline
                  onClick={function () {
                    setShowSaveForm(false);
                    setSaveLabel('');
                    setSaveDescription('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              color="link"
              size="sm"
              className="fb-save-trigger"
              onClick={function () {
                setShowSaveForm(true);
              }}
            >
              Save filter…
            </Button>
          )}
        </div>
      )}

      {/* ── Query preview ──────────────────────────────────────────────────── */}
      {showQuery && conditions.length > 0 && (
        <div className="fb-query-preview">
          <div className="fb-query-label">Sequelize where clause</div>
          <pre className="fb-query-code">{JSON.stringify(where, null, 2)}</pre>
        </div>
      )}

      {/* ── Modal ──────────────────────────────────────────────────────────── */}
      <FilterConditionModal
        isOpen={modalOpen}
        fields={fields}
        initial={editing}
        onSave={handleSave}
        onClose={closeModal}
      />
    </div>
  );
}
